import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { useTranslation } from 'react-i18next';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { getStatLabel } from '../../constants/dashboardStats.catalog';
import { isTrackableKpi, TRACKABLE_KPI_OPTIONS } from '../../constants/kpiTargets';
import { ZERO_ICONS } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { helpI18n } from '../../i18n/help';
import { KPILimit, useKPIStore } from '../../stores/kpi.store';
import { contentBorders } from '../../theme/createContentTheme';
import { StatisticType } from '../../types/dashboard.type';
import type { OrganizationType } from '../../types/organization.type';
import EmptyState from '../EmptyState';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

interface KPILimitsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Org type of the dashboard — picks org-specific KPI wording. */
  orgType?: OrganizationType | null;
  /** KPIs offered in the picker (defaults to every trackable statistic). */
  options?: StatisticType[];
  /** Knowledge Base try-it step 2: spotlight the form. */
  helpGuide?: boolean;
  onHelpGuideDone?: () => void;
  /** Self-setup tour tip inside this dialog. */
  tourTip?: 'kpiForm' | 'kpiList' | null;
  onTourNext?: () => void;
  onTourDismiss?: () => void;
  tourFormTitle?: string;
  tourFormSubtitle?: string;
  tourListTitle?: string;
  tourListSubtitle?: string;
  tourNextLabel?: string;
}

type Operator = KPILimit['operator'];

/** Display order and maths symbol for each comparison. */
const OPERATORS: Array<{ value: Operator; symbol: string; key: string }> = [
  { value: '>', symbol: '>', key: 'gt' },
  { value: '>=', symbol: '≥', key: 'gte' },
  { value: '<', symbol: '<', key: 'lt' },
  { value: '<=', symbol: '≤', key: 'lte' },
  { value: '=', symbol: '=', key: 'eq' },
];

const operatorMeta = (op: Operator) => OPERATORS.find((o) => o.value === op) ?? OPERATORS[0];

const EMPTY_DRAFT: Partial<KPILimit> = { name: '', value: 0, operator: '>', alertMessage: '', isEnabled: true };

/** "> 80" kept left-to-right so the comparison never mirrors in RTL. */
const RuleExpression: React.FC<{ symbol: string; value: string }> = ({ symbol, value }) => (
  <Box component="bdi" dir="ltr" sx={{ unicodeBidi: 'isolate', fontVariantNumeric: 'tabular-nums' }}>
    {symbol} {value}
  </Box>
);

/**
 * KPI limits & alerts: compose a threshold rule on an operational KPI, then
 * manage the saved rules (pause / delete). Rules persist in `useKPIStore`
 * (this browser only) and are matched to dashboard figures by metric key.
 */
const KPILimitsDialog: React.FC<KPILimitsDialogProps> = ({
  open,
  onClose,
  orgType = null,
  options,
  helpGuide = false,
  onHelpGuideDone,
  tourTip = null,
  onTourNext,
  onTourDismiss,
  tourFormTitle,
  tourFormSubtitle,
  tourListTitle,
  tourListSubtitle,
  tourNextLabel,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const fmt = useNumberFormat();
  const borders = contentBorders(theme);
  const { limits, addLimit, removeLimit, toggleLimit } = useKPIStore();
  const [draft, setDraft] = useState<Partial<KPILimit>>(EMPTY_DRAFT);

  const spotlight = helpGuide || Boolean(tourTip);
  const kpiOptions = (options ?? TRACKABLE_KPI_OPTIONS).filter(isTrackableKpi);
  const canAdd = Boolean(draft.name && draft.alertMessage?.trim());
  const label = (key: string) => getStatLabel(t, key, orgType);
  const operatorLabel = (op: Operator) => t(`kpiLimits.operators.${operatorMeta(op).key}`);
  // Operator labels are title case in English ("Greater Than"); lowercase them mid-sentence.
  const inSentence = (text: string) => ((i18n.language || 'en').startsWith('en') ? text.toLowerCase() : text);

  const handleAddLimit = () => {
    if (!canAdd) return;
    addLimit({
      id: Math.random().toString(36).substr(2, 9),
      name: draft.name as StatisticType,
      value: draft.value || 0,
      operator: draft.operator || '>',
      alertMessage: (draft.alertMessage ?? '').trim(),
      isEnabled: true,
    });
    setDraft(EMPTY_DRAFT);
    if (helpGuide) onHelpGuideDone?.();
  };

  const fieldSx = { '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="kpi-limits-title"
      PaperProps={{ sx: spotlight ? { overflow: 'visible' } : undefined }}
    >
      {/* Header: icon, title, purpose, close */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          pb: 2.5,
          borderBottom: `1px solid ${borders.default}`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${CONTENT_SHAPE.sm}px`,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          }}
        >
          <ZERO_ICONS.targets sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="kpi-limits-title" component="h2" variant="h6" sx={{ m: 0 }}>
            {t('kpiLimits.titleFull')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: '60ch' }}>
            {t('kpiLimits.subtitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t('common.close')} sx={{ mt: -0.5, mx: -0.5, color: 'text.secondary' }}>
          <CloseRounded />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 3,
          display: 'grid',
          gap: 3.5,
          ...(spotlight && { overflow: 'visible' }),
        }}
      >
        {/* Compose a rule */}
        <SetupCoachmark
          open={helpGuide || tourTip === 'kpiForm'}
          title={tourTip === 'kpiForm' ? (tourFormTitle ?? '') : tHelp('knowledgeBase.kpiGuideFormTitle')}
          subtitle={tourTip === 'kpiForm' ? (tourFormSubtitle ?? '') : tHelp('knowledgeBase.kpiGuideFormSubtitle')}
          onDismiss={() => {
            if (tourTip === 'kpiForm') onTourDismiss?.();
            else onHelpGuideDone?.();
          }}
          onNext={() => {
            if (tourTip === 'kpiForm') onTourNext?.();
            else onHelpGuideDone?.();
          }}
          nextLabel={
            tourTip === 'kpiForm'
              ? (tourNextLabel ?? tHelp('knowledgeBase.kpiGuideFormDone'))
              : tHelp('knowledgeBase.kpiGuideFormDone')
          }
          fullWidth
        >
          <Box id="self-setup-kpi-form" component="section" aria-labelledby="kpi-limits-new">
            <Typography id="kpi-limits-new" component="h3" variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {t('kpiLimits.addNewLimit')}
            </Typography>
            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: `${CONTENT_SHAPE.md}px`,
                border: `1px solid ${borders.default}`,
                bgcolor: 'background.default',
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1fr)' },
              }}
            >
              <TextField
                select
                size="small"
                fullWidth
                label={t('kpiLimits.kpi')}
                value={draft.name ?? ''}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
                sx={fieldSx}
              >
                {kpiOptions.map((stat) => (
                  <MenuItem key={stat} value={stat}>
                    {label(stat)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                fullWidth
                label={t('kpiLimits.operator')}
                value={draft.operator ?? '>'}
                onChange={(e) => setDraft({ ...draft, operator: e.target.value as Operator })}
                SelectProps={{
                  renderValue: (v) => (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      <OperatorSymbol symbol={operatorMeta(v as Operator).symbol} />
                      {operatorLabel(v as Operator)}
                    </Box>
                  ),
                }}
                sx={fieldSx}
              >
                {OPERATORS.map((op) => (
                  <MenuItem key={op.value} value={op.value} sx={{ gap: 1.25 }}>
                    <OperatorSymbol symbol={op.symbol} />
                    {t(`kpiLimits.operators.${op.key}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('kpiLimits.value')}
                value={draft.value ?? 0}
                onChange={(e) => setDraft({ ...draft, value: Number(e.target.value) })}
                inputProps={{ inputMode: 'decimal', step: 'any', dir: 'ltr' }}
                sx={fieldSx}
              />
              <TextField
                size="small"
                fullWidth
                label={t('kpiLimits.alertMessage')}
                placeholder={t('kpiLimits.alertMessagePlaceholder')}
                value={draft.alertMessage ?? ''}
                onChange={(e) => setDraft({ ...draft, alertMessage: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddLimit();
                }}
                sx={[fieldSx, { gridColumn: '1 / -1' }]}
              />
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, color: 'text.secondary' }}>
                  {draft.name ? (
                    <>
                      <NotificationsActiveOutlined aria-hidden sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="body2" component="p" sx={{ m: 0 }}>
                        {t('kpiLimits.alertWhen', {
                          name: label(draft.name),
                          operator: inSentence(operatorLabel(draft.operator ?? '>')),
                          value: fmt.number(draft.value ?? 0),
                        })}
                      </Typography>
                    </>
                  ) : null}
                </Box>
                <Button variant="contained" startIcon={<AddRounded />} onClick={handleAddLimit} disabled={!canAdd}>
                  {t('kpiLimits.addLimit')}
                </Button>
              </Box>
            </Box>
          </Box>
        </SetupCoachmark>

        {/* Saved rules */}
        <SetupCoachmark
          open={tourTip === 'kpiList'}
          title={tourListTitle ?? ''}
          subtitle={tourListSubtitle ?? ''}
          onDismiss={() => onTourDismiss?.()}
          onNext={() => onTourNext?.()}
          nextLabel={tourNextLabel}
          fullWidth
        >
          <Box id="self-setup-kpi-list" component="section" aria-labelledby="kpi-limits-current">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography id="kpi-limits-current" component="h3" variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('kpiLimits.currentLimits')}
              </Typography>
              {limits.length > 0 ? (
                <Chip size="small" label={<bdi>{fmt.number(limits.length)}</bdi>} sx={{ fontWeight: 600 }} />
              ) : null}
            </Box>

            {limits.length === 0 ? (
              <EmptyState
                icon={NotificationsNoneOutlined}
                title={t('kpiLimits.noLimits')}
                description={t('kpiLimits.emptyHint')}
                minHeight={160}
              />
            ) : (
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  border: `1px solid ${borders.default}`,
                  borderRadius: `${CONTENT_SHAPE.md}px`,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                {limits.map((limit, index) => {
                  const op = operatorMeta(limit.operator);
                  const name = label(limit.name);
                  return (
                    <Box
                      component="li"
                      key={limit.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        alignItems: 'center',
                        columnGap: 2,
                        rowGap: 1,
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        borderTop: index === 0 ? 0 : `1px solid ${borders.subtle}`,
                      }}
                    >
                      <Box sx={{ minWidth: 0, opacity: limit.isEnabled ? 1 : 0.55 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" component="p" sx={{ m: 0 }}>
                            {name}
                          </Typography>
                          <Tooltip title={operatorLabel(limit.operator)}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={<RuleExpression symbol={op.symbol} value={fmt.number(limit.value)} />}
                              sx={{
                                fontWeight: 600,
                                color: limit.isEnabled ? 'primary.main' : 'text.secondary',
                                borderColor: limit.isEnabled ? alpha(theme.palette.primary.main, 0.4) : borders.default,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.75, color: 'text.secondary' }}>
                          <NotificationsNoneOutlined aria-hidden sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                            {limit.alertMessage ||
                              t('kpiLimits.alertWhen', {
                                name,
                                operator: inSentence(operatorLabel(limit.operator)),
                                value: fmt.number(limit.value),
                              })}
                          </Typography>
                        </Box>
                        {!isTrackableKpi(limit.name) ? (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.75, color: 'warning.main' }}>
                            <WarningAmberRounded aria-hidden sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ m: 0, fontWeight: 500 }}>
                              {t('kpiLimits.untrackable')}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: limit.isEnabled ? 'primary.main' : 'text.disabled',
                            minWidth: 56,
                            textAlign: 'end',
                          }}
                        >
                          {limit.isEnabled ? t('common.active') : t('kpiLimits.paused')}
                        </Typography>
                        <Switch
                          size="small"
                          checked={limit.isEnabled}
                          onChange={() => toggleLimit(limit.id)}
                          inputProps={{ 'aria-label': `${name} · ${t('common.active')}` }}
                        />
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            onClick={() => removeLimit(limit.id)}
                            aria-label={`${t('common.delete')} · ${name}`}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutlineRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </SetupCoachmark>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2, borderTop: `1px solid ${borders.default}` }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Compact maths symbol tile used in the condition picker. */
const OperatorSymbol: React.FC<{ symbol: string }> = ({ symbol }) => (
  <Box
    component="span"
    dir="ltr"
    aria-hidden
    sx={(theme) => ({
      display: 'inline-grid',
      placeItems: 'center',
      width: 22,
      height: 22,
      flexShrink: 0,
      borderRadius: `${CONTENT_SHAPE.xs}px`,
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: 'primary.main',
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1,
    })}
  >
    {symbol}
  </Box>
);

export default KPILimitsDialog;
